/** @jsxRuntime automatic */
import { useId, type Ref, type SVGProps } from "react";
import { markPlug } from "./brand-plug";
import { cn } from "./utils";

/*
 * Winche brand (spec 07 › Marca). Pure vector paths, no <text>: the isotype is
 * a W whose last stroke rises into the hook of a winch ("tu agente te saca del
 * pozo"); the wordmark is Outfit 600 outlined. The same path strings feed the
 * favicon, PWA icons, email PNG and PDF footer (WINCHE_MARK_* exports), so
 * every surface draws the same mark.
 */

/** Isotype glyph on a 32×32 grid (stroke, round caps/joins). */
export const WINCHE_MARK_GLYPH_PATH =
  "M6.5 9.5 10.75 22.5 16 12.5 21.25 22.5 24.55 11.9C25.3 9.45 23.35 7.85 21.55 8.95";
export const WINCHE_MARK_STROKE_WIDTH = 3;
export const WINCHE_MARK_VIEWBOX = "0 0 32 32";
/** Tile radius on the 32 grid (8 = 25 %, the app-icon corner). */
export const WINCHE_MARK_TILE_RADIUS = 8;

/** "Winche" in Outfit 600, outlined. Cap height 70.3 on this grid; `h` ascends to −2. */
export const WINCHE_WORDMARK_PATH =
  "M25 70.3L2.2 0L15.6 0L32.5 54.1L28 54.1L44.6 0L54.7 0L71.3 54.1L66.7 54.1L83.7 0L97.1 0L74.2 70.3L64.1 70.3L47.4 16.3L51.8 16.3L35.2 70.3ZM105 70.3L105 22L118.2 22L118.2 70.3ZM111.6 14.2Q108.4 14.2 106.3 12.05Q104.2 9.9 104.2 6.7Q104.2 3.6 106.3 1.4Q108.4 -0.8 111.6 -0.8Q114.9 -0.8 116.95 1.4Q119 3.6 119 6.7Q119 9.9 116.95 12.05Q114.9 14.2 111.6 14.2ZM162.3 70.3L162.3 42.6Q162.3 38.3 159.6 35.6Q156.9 32.9 152.6 32.9Q149.8 32.9 147.6 34.1Q145.4 35.3 144.15 37.5Q142.9 39.7 142.9 42.6L137.8 40Q137.8 34.3 140.25 30.05Q142.7 25.8 147.05 23.4Q151.4 21 156.9 21Q162.2 21 166.4 23.65Q170.6 26.3 173 30.55Q175.4 34.8 175.4 39.7L175.4 70.3ZM129.8 70.3L129.8 22L142.9 22L142.9 70.3ZM208.8 71.3Q201.4 71.3 195.5 68Q189.6 64.7 186.2 59Q182.8 53.3 182.8 46.2Q182.8 39 186.2 33.3Q189.6 27.6 195.55 24.25Q201.5 20.9 208.8 20.9Q214.5 20.9 219.3 23Q224.1 25.1 227.6 29L219.2 37.5Q217.3 35.4 214.65 34.3Q212 33.2 208.8 33.2Q205.2 33.2 202.35 34.85Q199.5 36.5 197.85 39.35Q196.2 42.2 196.2 46.1Q196.2 49.9 197.85 52.85Q199.5 55.8 202.35 57.45Q205.2 59.1 208.8 59.1Q212.1 59.1 214.75 57.95Q217.4 56.8 219.3 54.6L227.8 63.1Q224.1 67.1 219.3 69.2Q214.5 71.3 208.8 71.3ZM267.7 70.3L267.7 42.6Q267.7 38.3 265 35.6Q262.3 32.9 258 32.9Q255.2 32.9 253 34.1Q250.8 35.3 249.55 37.5Q248.3 39.7 248.3 42.6L243.2 40Q243.2 34.3 245.6 30.05Q248 25.8 252.25 23.4Q256.5 21 262 21Q267.5 21 271.75 23.4Q276 25.8 278.4 30Q280.8 34.2 280.8 39.7L280.8 70.3ZM235.2 70.3L235.2 -2L248.3 -2L248.3 70.3ZM314.5 71.3Q306.9 71.3 301 68.05Q295.1 64.8 291.65 59.1Q288.2 53.4 288.2 46.1Q288.2 38.9 291.55 33.25Q294.9 27.6 300.7 24.25Q306.5 20.9 313.6 20.9Q320.6 20.9 325.95 24.05Q331.3 27.2 334.35 32.65Q337.4 38.1 337.4 45Q337.4 46.3 337.25 47.65Q337.1 49 336.7 50.7L296.1 50.8L296.1 41L330.6 40.9L325.3 45Q325.1 40.7 323.75 37.8Q322.4 34.9 319.85 33.35Q317.3 31.8 313.6 31.8Q309.7 31.8 306.8 33.55Q303.9 35.3 302.35 38.45Q300.8 41.6 300.8 45.9Q300.8 50.3 302.45 53.55Q304.1 56.8 307.2 58.55Q310.3 60.3 314.4 60.3Q318.1 60.3 321.1 59.05Q324.1 57.8 326.3 55.3L334 63Q330.5 67.1 325.45 69.2Q320.4 71.3 314.5 71.3Z";
export const WINCHE_WORDMARK_VIEWBOX = "0 -3 340 75";
const WORDMARK_RATIO = 340 / 75;

/**
 * - `brand`: violet tile, white W (default — app, PWA, OG, email).
 * - `ink`: foreground tile, background-colored W (monochrome contexts).
 * - `mono`: the W alone in `currentColor` (notification badge, tiny sizes, PDF).
 */
export type WincheMarkTone = "brand" | "ink" | "mono";

/**
 * What the W is doing (spec 07 › Momentos de marca, «La W viva»): `rest`
 * today's mark · `working` it breathes while a run really works · `done` its
 * ink becomes a check · `lift` the hook-lift of the job (L4 only).
 */
export type WincheMarkState = "rest" | "working" | "done" | "lift";

export interface WincheMarkProps extends Omit<SVGProps<SVGSVGElement>, "ref"> {
  /** The `<svg>` (a breathing W puts its loop in phase with `useLoopPhase`). */
  ref?: Ref<SVGSVGElement>;
  /** Pixel size (square). Minimum legible: 16. Sidebar 24, splash 56. */
  size?: number;
  tone?: WincheMarkTone;
  /** Accessible name; omit when a visible "Winche" sits next to it. */
  title?: string;
  /**
   * A W that moves passes its state, `rest` included (a mark that never
   * moves passes none): `working` breathes on the one heartbeat, paused out
   * of view and with the tab hidden, and settles at 1 in 200 when it stops;
   * `done` hands its ink to a check (the W retracts into its hook in 160, the
   * check writes itself in 240 from +80, the tile turns success in 200);
   * `lift` writes the W to its hook in 480 and the tile tugs up 2 px. `done`
   * and `lift` play when they arrive after the mount: mounted in them, the
   * end is there. A mark at rest is today's DOM and pixels.
   */
  state?: WincheMarkState;
  /**
   * The wait's only indicator (a splash, the handoff, the first search):
   * while it works at ≥ 32 px the pen passes too, a wider stroke at .4 that
   * runs along the W to its hook.
   */
  trace?: boolean;
  /** A persistent mark (InboxPulse): a check holds `--duration-hold`, then retracts and the W writes itself back (320). */
  hold?: boolean;
  /**
   * Each new value after the mount beats once, 1 → 1.06 → 1 in 240 (marks of
   * 20–56 px); `useWorkSignal` already spaces them ≥ 2 s and only for a
   * meaningful event. With reduced motion, a dip to .75.
   */
  beat?: number;
  /** The wait shows a bar too (two motors): with reduced motion the W holds still and the bar is the one live indicator. */
  withBar?: boolean;
}

/** The isotype. Clear space around it: half the W's width. */
export function WincheMark(props: WincheMarkProps) {
  const titleId = useId();
  // The W that moves (`brand-live.tsx`) plugs itself in when its module loads (brand-plug.ts).
  const Live = props.state !== undefined || props.beat !== undefined ? markPlug.live : undefined;
  if ((import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV && !Live && (props.state ?? "rest") !== "rest") {
    console.warn("[motion] A W that moves needs its live module: it comes with useWorkSignal (and AiTask).");
  }
  if (Live) return <Live {...props} />;
  // At rest: today's mark (the motion props never reach the DOM).
  const { size = 24, tone = "brand", title, className, state: _state, trace: _trace, hold: _hold, beat: _beat, withBar: _withBar, ...rest } = props;
  const tile = tone !== "mono";
  return (
    <svg
      width={size}
      height={size}
      viewBox={WINCHE_MARK_VIEWBOX}
      fill="none"
      role={title ? "img" : undefined}
      aria-labelledby={title ? titleId : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      className={cn("shrink-0", className)}
      {...rest}
    >
      {title ? <title id={titleId}>{title}</title> : null}
      {tile ? (
        <rect
          width="32"
          height="32"
          rx={WINCHE_MARK_TILE_RADIUS}
          className={tone === "brand" ? "fill-brand" : "fill-foreground"}
        />
      ) : null}
      <path
        d={WINCHE_MARK_GLYPH_PATH}
        strokeWidth={WINCHE_MARK_STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={
          tone === "brand" ? "stroke-brand-foreground" : tone === "ink" ? "stroke-background" : "stroke-current"
        }
      />
    </svg>
  );
}

export interface WincheWordmarkProps extends Omit<SVGProps<SVGSVGElement>, "ref"> {
  /** Cap height in px (the rendered height is ≈1.07×). Sidebar 14, auth 20. */
  height?: number;
  title?: string;
}

/** "Winche" outlined in Outfit 600, `currentColor`. */
export function WincheWordmark({ height = 14, title, className, ...props }: WincheWordmarkProps) {
  const titleId = useId();
  const h = Math.round(height * (75 / 70.3) * 100) / 100;
  return (
    <svg
      height={h}
      width={Math.round(h * WORDMARK_RATIO * 100) / 100}
      viewBox={WINCHE_WORDMARK_VIEWBOX}
      role={title ? "img" : undefined}
      aria-labelledby={title ? titleId : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      className={cn("shrink-0 fill-current", className)}
      {...props}
    >
      {title ? <title id={titleId}>{title}</title> : null}
      <path d={WINCHE_WORDMARK_PATH} />
    </svg>
  );
}

export interface LogoProps {
  /** `lockup` mark + wordmark (default) · `mark` isotype only · `wordmark` name only. */
  variant?: "lockup" | "mark" | "wordmark";
  /** Mark size in px; the wordmark scales with it. */
  size?: number;
  tone?: WincheMarkTone;
  /** Accessible name for the whole logo (default "Winche"). Pass `""` when decorative. */
  label?: string;
  className?: string;
}

/**
 * Winche logo for app chrome (sidebar, app bar, auth, onboarding, splash).
 * Renders a single labelled image: screen readers hear "Winche" once.
 */
export function Logo({ variant = "lockup", size = 24, tone = "brand", label = "Winche", className }: LogoProps) {
  const a11y = label ? { role: "img" as const, "aria-label": label } : { "aria-hidden": true as const };
  if (variant === "mark") {
    return (
      <span className={cn("inline-flex", className)} {...a11y}>
        <WincheMark size={size} tone={tone} />
      </span>
    );
  }
  if (variant === "wordmark") {
    return (
      <span className={cn("inline-flex text-foreground", className)} {...a11y}>
        <WincheWordmark height={Math.round(size * 0.6)} />
      </span>
    );
  }
  return (
    <span className={cn("inline-flex items-center gap-2 text-foreground", className)} {...a11y}>
      <WincheMark size={size} tone={tone} />
      <WincheWordmark height={Math.round(size * 0.6)} />
    </span>
  );
}
